import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
const PromptInterface = ({ prompt, onPromptChange, onSubmit, isLoading = false, placeholder = "Enter your prompt here...", maxLength = 14000, // Default limit (updated to match SuperIntelligent)
showStats = true, submitButtonText = "Generate", disabled = false, buttonDisabled = false, autoFocus = false }) => {
    // 📊 CHARACTER LIMIT ENFORCEMENT & WARNING SYSTEM
    const characterStats = useMemo(() => {
        const remaining = maxLength - prompt.length;
        const percentage = (prompt.length / maxLength) * 100;
        return {
            current: prompt.length,
            limit: maxLength,
            remaining,
            percentage,
            isNearLimit: percentage >= 85,
            isAtLimit: percentage >= 100,
            warningLevel: percentage >= 95 ? 'danger' : percentage >= 85 ? 'warning' : 'normal'
        };
    }, [prompt.length, maxLength]);
    // Handle prompt changes with character limit enforcement
    const handlePromptChange = useCallback((e) => {
        const newValue = e.target.value;
        // 🚨 ENFORCE CHARACTER LIMIT - truncate if exceeding
        if (newValue.length > maxLength) {
            const truncatedValue = newValue.substring(0, maxLength);
            onPromptChange(truncatedValue);
            return;
        }
        onPromptChange(newValue);
    }, [onPromptChange, maxLength]);
    // Handle submit with validation
    const handleSubmit = useCallback(() => {
        if (disabled || isLoading || !prompt.trim() || characterStats.isAtLimit) {
            return;
        }
        onSubmit();
    }, [disabled, isLoading, prompt, characterStats.isAtLimit, onSubmit]);
    // Handle Enter key with Ctrl/Cmd modifier
    const handleKeyDown = useCallback((e) => {
        if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
            e.preventDefault();
            handleSubmit();
        }
    }, [handleSubmit]);
    // Character counter color classes
    const getCounterClass = () => {
        switch (characterStats.warningLevel) {
            case 'danger': return 'text-red-600 font-bold';
            case 'warning': return 'text-yellow-600 font-semibold';
            default: return 'text-gray-500';
        }
    };
    return (_jsxs("div", { className: "w-full space-y-3", children: [_jsxs("div", { className: "relative", children: [_jsx("textarea", { value: prompt, onChange: handlePromptChange, onKeyDown: handleKeyDown, placeholder: placeholder, disabled: disabled || isLoading, autoFocus: autoFocus, rows: 6, className: `
            w-full px-4 py-3 border rounded-lg resize-none focus:outline-none focus:ring-2 transition-all duration-200
            ${characterStats.isNearLimit
                            ? 'border-yellow-300 focus:ring-yellow-500 bg-yellow-50'
                            : 'border-gray-300 focus:ring-blue-500'}
            ${characterStats.isAtLimit
                            ? 'border-red-400 focus:ring-red-500 bg-red-50'
                            : ''}
            ${disabled || isLoading ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
          ` }), characterStats.isNearLimit && (_jsx("div", { className: `
            absolute top-2 right-2 px-2 py-1 text-xs rounded-md font-medium
            ${characterStats.isAtLimit
                            ? 'bg-red-100 text-red-700 border border-red-200'
                            : 'bg-yellow-100 text-yellow-700 border border-yellow-200'}
          `, children: characterStats.isAtLimit ? 'CHARACTER LIMIT REACHED' : 'APPROACHING LIMIT' }))] }), _jsxs("div", { className: "flex items-center justify-between", children: [showStats && (_jsxs("div", { className: "flex items-center space-x-4 text-sm", children: [_jsxs("span", { className: getCounterClass(), children: [characterStats.current.toLocaleString(), " / ", characterStats.limit.toLocaleString(), " characters"] }), characterStats.remaining >= 0 && (_jsxs("span", { className: "text-gray-400", children: ["(", characterStats.remaining.toLocaleString(), " remaining)"] })), characterStats.isNearLimit && !characterStats.isAtLimit && (_jsx("span", { className: "text-yellow-600 text-xs", children: "\u26A0\uFE0F Approaching character limit" })), characterStats.isAtLimit && (_jsx("span", { className: "text-red-600 text-xs font-medium", children: "\uD83D\uDEAB Character limit reached - reduce text to continue" }))] })), _jsx("button", { onClick: handleSubmit, disabled: disabled || buttonDisabled || isLoading || !prompt.trim() || characterStats.isAtLimit, className: `
            px-6 py-2 rounded-lg font-medium transition-all duration-200 min-w-[120px]
            ${disabled || buttonDisabled || isLoading || !prompt.trim() || characterStats.isAtLimit
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700 text-white hover:shadow-md'}
          `, children: isLoading ? (_jsxs("div", { className: "flex items-center justify-center space-x-2", children: [_jsx("div", { className: "animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" }), _jsx("span", { children: "Processing..." })] })) : (submitButtonText) })] }), _jsx("div", { className: "text-xs text-gray-400 text-center", children: "Press Ctrl+Enter (Cmd+Enter on Mac) to submit" })] }));
};
export default PromptInterface;
